/**
 * The four glyphs the phone's tab bar draws: Hugeicons' stroke-rounded set
 * (https://hugeicons.com/icons/stroke-rounded), which is the set the design
 * takes its icons from — see the note at the top of `components/ui/Icons.tsx`.
 *
 * These are the set's own path data, via `@hugeicons/core-free-icons`, not
 * anything traced by hand. One of the four is the very drawing a rail asset
 * was exported from: put `Enquiries.svg` beside `Star` and they are identical.
 * So for that one the phone and the desktop are not merely consistent, they
 * are the same icon.
 *
 * `AnalyticsUp` used to be a second such pair, for the Revenue circle. Revenue
 * has moved into the account menu — where it is drawn from `NAV_ICONS` like
 * every other row on that screen — and My task has the circle, so the glyph
 * went with it rather than being left here unused.
 *
 * The task mark that replaced it is the other way round again: it is the rail's
 * SVG itself, restruck in `currentColor` so a white glyph on a filled circle is
 * possible at all. See `TaskGlyph`.
 *
 * The home mark is the tab that answers to no rail item's drawing, because it
 * answers to no rail item's name either: the bar's first tab is Home, where
 * the rail calls the same destination Dashboard and draws it as the 2×2 grid
 * of `dashboard.svg`. A house is what a phone's first tab is read by, and a
 * grid of squares beside the word Home reads as neither.
 *
 * The clients mark is the remaining exception, and only in style. The rail's
 * `clients-active.svg` is an *outlined* export — the stroke expanded into
 * filled compound paths — which is why it stood wider and heavier than
 * anything beside it in a row, the second figure breaking into two detached
 * crescents that read as a stray limb. `UserMultiple02` is that same drawing
 * in the set's own stroke, and it is the one of the set's several two-person
 * marks that matches: the body is a closed, rounded shape, the way the rail's
 * is. Its near neighbour `UserMultiple03` is the same subject with the body
 * left *open* at the foot — two straight legs rather than a rounded base —
 * and beside the rail's oval it reads as a different icon.
 *
 * ## Why the coordinates are not the shipped ones
 *
 * Two reasons, both geometry and neither of them a redraw.
 *
 * The first is size. Hugeicons optically balances its glyphs for use on their
 * own, so inside the same 24×24 box they draw to different extents: the home
 * mark 19 units and the clients and enquiry marks a full 20. The task mark is
 * the rail's own asset rather than the set's and comes in at 15 of its own 18.
 * That is right for an icon seen by itself and wrong for four in a row, where
 * the eye reads the difference as some tabs being bigger than others. So every
 * glyph's geometry is scaled about the centre of its box to draw at 18 — the
 * extent of the dashboard grid this bar used to open with, kept now that the
 * grid is gone so that swapping the first tab's glyph could not resize the
 * three beside it. The stroke is deliberately *not* scaled with it and stays
 * at the set's 1.5 everywhere, so evening out the sizes cannot disturb the
 * weights: one size, one weight, four glyphs that read as one set.
 *
 * The second applies to the clients mark alone, which is mirrored. The set
 * ships it with the second figure behind the *left* shoulder and the rail
 * draws it behind the right; a mirror is what makes the phone and the desktop
 * show the same thing, and on a symmetrical subject like this one it costs
 * the drawing nothing.
 *
 * Stroked in `currentColor`, so a call site colours them by setting `text-*`
 * on an ancestor. That is why the `.nav-glyph` rules that used to sit in
 * `index.css` are gone — they existed only to override the `stroke="#00424B"`
 * baked into the exported assets.
 *
 * Only the tab bar uses these. The rail still draws the exported assets from
 * `NAV_ICONS`, and swapping those is a change to the desktop.
 */
import React from "react";
import { NavIcon } from "./navIcons";

/** What the set ships every one of these with. */
const glyphProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

/**
 * A house with a doorway cut out of it.
 *
 * Hugeicons `home-09`, from `cdn.hugeicons.com/icons/home-09-stroke-rounded.svg` — scaled ×0.947322 from the 19.0009 units the set ships. The set's several house marks differ in what is drawn inside them: `home-01` is the bare outline with nothing in it, `home-04` has a line across the floor and `home-05` a free-standing door that does not touch the wall. `home-09` cuts the doorway out of the outline itself, in one path, which is the one that still reads as a door at 22px.
 */
export const HomeGlyph: NavIcon = (props) => (
  <svg {...glyphProps} {...props}>
    <path d="M12.947 13.424H11.053C10.268 13.424 9.632 14.06 9.632 14.845V20.053C9.632 20.577 9.206 21.003 8.683 20.997C6.48 20.975 5.256 20.842 4.445 20.031C3.474 19.059 3.474 17.496 3.474 14.37V11.992C3.474 10.4 3.474 9.603 3.811 8.914C4.148 8.225 4.777 7.736 6.034 6.758L7.929 5.284C9.885 3.763 10.862 3.003 12 3.003C13.138 3.003 14.115 3.763 16.071 5.284L17.966 6.758C19.223 7.736 19.852 8.225 20.189 8.914C20.526 9.603 20.526 10.4 20.526 11.992V14.37C20.526 17.496 20.526 19.059 19.555 20.031C18.744 20.842 17.52 20.975 15.317 20.997C14.794 21.003 14.368 20.577 14.368 20.053V14.845C14.368 14.06 13.732 13.424 12.947 13.424Z" />
  </svg>
);

/**
 * One figure with a second behind its right shoulder, both on a closed,
 * rounded body.
 *
 * Hugeicons `UserMultiple02`, the drawing behind the rail's `clients-active.svg` — scaled ×0.9 from the 20 units the set ships, mirrored to put the second figure on the right, as the rail has it.
 */
export const ClientsGlyph: NavIcon = (props) => (
  <svg {...glyphProps} {...props}>
    <path d="M6.15 7.5C6.15 9.488 7.762 11.1 9.75 11.1C11.738 11.1 13.35 9.488 13.35 7.5C13.35 5.512 11.738 3.9 9.75 3.9C7.762 3.9 6.15 5.512 6.15 7.5Z" />
    <path d="M15.6 11.1C17.091 11.1 18.3 9.891 18.3 8.4C18.3 6.909 17.091 5.7 15.6 5.7" />
    <path d="M9.75 13.8C13.969 13.8 16.5 16.05 16.5 18.3C16.5 19.294 15.744 20.1 14.812 20.1H4.688C3.756 20.1 3 19.294 3 18.3C3 16.05 5.531 13.8 9.75 13.8Z" />
    <path d="M19.2 19.199H19.65C20.396 19.199 21 18.509 21 17.656C21 16.206 19.854 14.755 17.85 14.125" />
  </svg>
);

/**
 * A five-pointed star with rounded points.
 *
 * Hugeicons `Star`, the drawing behind the rail's `Enquiries.svg` — scaled ×0.90003 from the 19.9993 units the set ships.
 */
export const EnquiryGlyph: NavIcon = (props) => (
  <svg {...glyphProps} {...props}>
    <path d="M13.555 4.3L15.139 7.493C15.355 7.938 15.931 8.365 16.417 8.446L19.287 8.927C21.123 9.236 21.555 10.578 20.232 11.903L18.001 14.154C17.623 14.535 17.416 15.27 17.533 15.796L18.172 18.581C18.676 20.786 17.515 21.639 15.58 20.487L12.889 18.881C12.403 18.591 11.602 18.591 11.107 18.881L8.416 20.487C6.49 21.639 5.321 20.777 5.825 18.581L6.463 15.796C6.58 15.27 6.373 14.535 5.996 14.154L3.764 11.903C2.45 10.578 2.873 9.236 4.709 8.927L7.579 8.446C8.056 8.365 8.632 7.938 8.848 7.493L10.432 4.3C11.296 2.566 12.7 2.566 13.555 4.3Z" />
  </svg>
);


/**
 * A board with a clip standing proud of its top edge, and a page lifting away
 * behind it.
 *
 * The rail's own drawing, from `sidebar/my task.svg`, rather than anything out
 * of the icon set: the design team drew this one, and the circle on the phone's
 * bar leads to the same screen the rail's My task item does. Where a shared
 * asset exists it wins — see `.claude/skills/hugeicons`, which says so — and
 * this is the exception that proves the rest of this file, where the rail's
 * assets are outlined exports that would not sit in a row with the set's own
 * strokes.
 *
 * Two things were done to it on the way in, both geometry and neither a
 * redraw. Its own 18x18 box was scaled x1.2 about the centre of the drawing so
 * it lands in the 24x24 box the glyphs beside it use, at the same 18-unit
 * extent they are all evened out to. And its baked `stroke="#00424B"` is gone,
 * so the mark takes the colour of whatever it is standing on — white on the
 * filled circle, which the rail's own asset could never have done.
 */
export const TaskGlyph: NavIcon = (props) => (
  <svg {...glyphProps} {...props}>
    <path d="M11.55 5.7C14.096 5.7 15.368 5.7 16.159 6.491C16.95 7.282 16.95 8.554 16.95 11.1C16.95 18.3 20.55 18.3 20.55 18.3H7.715C7.42 18.3 7.272 18.3 6.937 18.213C6.603 18.126 6.549 18.096 6.443 18.036C5.417 17.463 3.45 15.757 3.45 11.1C3.45 8.554 3.45 7.282 4.241 6.491C5.032 5.7 6.304 5.7 8.85 5.7" />
    <path d="M3.45 10.2V15.6C3.45 18.146 3.45 19.418 4.241 20.209C5.032 21 6.304 21 8.85 21H11.618C14.164 21 15.437 21 16.228 20.209C16.669 19.768 16.864 19.178 16.95 18.3" />
    <path d="M11.55 4.35V7.05C11.55 7.469 11.55 7.679 11.482 7.844C11.39 8.065 11.215 8.24 10.994 8.331C10.829 8.4 10.619 8.4 10.2 8.4C9.781 8.4 9.571 8.4 9.406 8.331C9.185 8.24 9.01 8.065 8.919 7.844C8.85 7.679 8.85 7.469 8.85 7.05V4.35C8.85 3.931 8.85 3.721 8.919 3.556C9.01 3.335 9.185 3.16 9.406 3.069C9.571 3 9.781 3 10.2 3C10.619 3 10.829 3 10.994 3.069C11.215 3.16 11.39 3.335 11.482 3.556C11.55 3.721 11.55 3.931 11.55 4.35Z" />
  </svg>
);
